
// Switched from react-bootstrap Modal to Mantine Modal
// Also use Mantine Button and Loader for consistency
import {EOrderStatus, Order, orderStore} from '../../../../../redux/entities/inventory/order';
import {CommonState} from '../../../../../redux';
import {bindActionCreators, Dispatch} from 'redux';
import { itemStore } from '../../../../../redux/entities/inventory/item';
import { categoryStore } from '../../../../../redux/entities/inventory/category';
import { programStore } from '../../../../../redux/entities/inventory/program';
import { houseStore } from '../../../../../redux/entities/inventory/house';
import {connect} from 'react-redux';
import {SubmitProps} from '../../../../../types';
import React, { FormEvent, useRef, useState } from 'react';
import { Form, Formik, FormikErrors } from 'formik';
// removed legacy layout helpers; using Mantine Grid like Landing page
import {AxiosError} from 'axios';
import {getErrorResponseMessage} from '../../../../../util/http';
import {AppTheme} from '../../../../../appTheme';
import { OrderItem } from '../../../../../redux/entities/inventory/joining/orderItem';
import IconButton from '../../../../../components/util/widgets/IconButton/IconButton';
import useStandardPrint from '../../../../../hooks/useStandardPrint/useStandardPrint';
// New imports to reuse the public landing form
import { Grid, Group, Text, Divider, Button, Select as MantineSelect } from '@mantine/core';
import { WithdrawalSchema } from '../../../../public/Landing/WithdrawalSchema';
import OrderForm from '../../../../../components/layout/Order/OrderForm';
import { formatCentsToUSD } from '../../../../../util';
import StandardModal from '../../../../../components/util/StandardModal/StandardModal';
import AdministratorComponent from "../../../../../components/util/RoleGatedComponent/AdministratorComponent";

const orderStatusDropdownOptions = [
  {value: EOrderStatus.Unapproved, label: 'Unapproved'},
  {value: EOrderStatus.Approved, label: 'Approved'},
  {value: EOrderStatus.Cancelled, label: 'Cancelled'}
];

const getOrderStatus = (val: EOrderStatus) => orderStatusDropdownOptions.find(o => o.value === val) ?? orderStatusDropdownOptions[0];

const ConnectedProgramValue = connect((state: CommonState, ownProps: {order: any}) => ({
  program: programStore.selectors.getById(state)(ownProps.order?.programId)
}))(({program, order}: {program: any; order: any}) => (
  <Text>{program?.name || order?.consumerInformation?.programOrDepartment || '-'}</Text>
));

const ConnectedHouseValue = connect((state: CommonState, ownProps: {order: any}) => ({
  house: houseStore.selectors.getById(state)(ownProps.order?.houseId)
}))(({house, order}: {house: any; order: any}) => (
  <Text>{house?.name || order?.consumerInformation?.houseOrNeighborhood || '-'}</Text>
));

interface ItemForm {
  id: string;
  orderItems: OrderItem[];
}

export interface OrderUpdate extends ItemForm {
  orderStatus: EOrderStatus;
  programId: string;
  houseId: string;
  isFromWarehouse?: boolean;
}

type Props = SubmitProps & {
  show: boolean;
  order: Order;
  /** When true, the modal opens in read-only view mode showing plain text instead of inputs */
  readOnlyInitial?: boolean;
} & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const OrderModal = (props: Props) => {
  const {show, onCancel, onSubmit, order, readOnlyInitial, getItemById, actions: {upsertOrder}} = props;
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentOrderStatus, setCurrentOrderStatus] = useState(getOrderStatus(order.orderStatus));
  const printRef = useRef(null);
  const handlePrint = useStandardPrint(printRef);
  // Local flag to control read-only vs edit mode inside the modal
  const [isReadOnly, setIsReadOnly] = useState<boolean>(!!readOnlyInitial);

  const processSubmit = async (
    e: FormEvent<HTMLFormElement>,
    association: Order,
    validate: (values: Order) => Promise<FormikErrors<Order>>,
    formikHandleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void,
    setTouched: (touched: any, shouldValidate?: boolean) => void
  ) => {
    setIsSubmitting(true);
    setErrorMessage('');
    e.persist();
    e.preventDefault();
    console.log('submitting');
    const errors = await validate(association);
    if (Object.values(errors).length !== 0) {
      setTouched({
        consumerInformation: {
          name: true,
          jobTitle: true,
          emailAddress: true,
          phoneNumber: true,
          everythingFound: true,
          everythingFoundComment: true,
          additionalComment: true,
        },
        programId: true,
        houseId: true,
        isFromWarehouse: true,
        orderItems: true,
      }, false);
      formikHandleSubmit(e);
      setIsSubmitting(false);
    } else {
      try {
        // Filter out empty order items similar to public landing behavior
        const filteredItems = (association.orderItems ?? []).filter(oi =>
          ((oi?.storeQuantity !== undefined || oi?.warehouseQuantity !== undefined) && (oi?.storeQuantity > 0 || oi?.warehouseQuantity > 0)) ||
          (oi?.itemOptions?.length > 0 && oi?.itemOptions?.find(o => o.quantity > 0))
        );
        const request: OrderUpdate = {
          ...association,
          orderItems: filteredItems,
          id: order.id,
          orderStatus: currentOrderStatus.value,
          programId: association.programId,
          houseId: association.houseId,
          isFromWarehouse: association.isFromWarehouse
        };
        console.log(JSON.stringify(request))
        await upsertOrder(request);
        await onSubmit();
      } catch (e: AxiosError | any) {
        setErrorMessage(getErrorResponseMessage(e));
      }
    }
    setIsSubmitting(false);
  };

  // Buttons provided by StandardModal footer

  const modalTitle = (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <h1 style={{color: AppTheme.colors.themeGreen}}>Withdraw Order</h1>
          {/* Push controls to the far right so they can hug the modal edge */}
          <Group style={{ display: 'flex', marginLeft: 'auto', marginRight: '2rem', fontWeight: 'initial', fontSize: '1.2rem', alignItems: 'center' }}>
            <MantineSelect
              styles={{
                input: { border: '1px solid', borderColor: AppTheme.colors.themePurple, opacity: isReadOnly ? 0.6 : 1, width: '160px' }
              }}
              disabled={isReadOnly}
              withinPortal
              value={currentOrderStatus.value.toString()}
              data={orderStatusDropdownOptions.map(o => ({ value: o.value.toString(), label: o.label }))}
              onChange={(newValue) => {
                const status = orderStatusDropdownOptions.find(o => o.value.toString() === newValue);
                if (status) setCurrentOrderStatus(status);
              }}
            />
            {order?.id && (
              <AdministratorComponent>
                <Button style={{ marginLeft: '12px', backgroundColor: AppTheme.colors.themePurple }} onClick={() => setIsReadOnly(!isReadOnly)}>
                  {isReadOnly ? 'Edit' : 'View'}
                </Button>
              </AdministratorComponent>
            )}
          </Group>
        </div>
  );

  const formId = 'order-modal-form';

  return (
    <StandardModal
      opened={show}
      onCancel={onCancel}
      size={'70vw'}
      title={modalTitle}
      submitText={isReadOnly ? 'Close' : 'Submit'}
      cancelText={isReadOnly ? 'Close' : 'Cancel'}
      submitDisabled={isSubmitting || !!isReadOnly}
      loading={isSubmitting}
      showSubmit={!isReadOnly}
      formId={isReadOnly ? undefined : formId}
      onSubmit={isReadOnly ? onCancel : undefined}
      errorMessage={errorMessage}
    >
      <Formik<Order>
        initialValues={order}
        validationSchema={WithdrawalSchema}
        validateOnBlur={false}
        onSubmit={(vals, { setSubmitting }) => undefined}
      >
        {({ values, setFieldValue, handleSubmit, validateForm, setTouched }) => (
          <Form onSubmit={(e) => processSubmit(e, values, validateForm, handleSubmit, setTouched)} id={formId}>
              {isReadOnly ? (
                <Grid gutter="xl" style={{ marginTop: '1rem' }}>
                  <Grid.Col
                    span={12}
                    lg={6}
                    // Ensure long, unbroken content (like emails, URLs, or IDs) wraps within the column
                    style={{
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <Grid gutter="md">
                      <Grid.Col span={12}>
                        <h2 style={{ color: AppTheme.colors.themeGreen }}>Contact Information</h2>
                      </Grid.Col>
                      <>
                        <Grid.Col span={12} sm={6}>
                          <Text fw={600} size="sm">Your name</Text>
                          <Text>{values.consumerInformation?.name || '-'}</Text>
                        </Grid.Col>
                        <Grid.Col span={12} sm={6}>
                          <Text fw={600} size="sm">Job title</Text>
                          <Text>{values.consumerInformation?.jobTitle || '-'}</Text>
                        </Grid.Col>
                        <Grid.Col span={12} sm={6}>
                          <Text fw={600} size="sm">Email address</Text>
                          <Text>{values.consumerInformation?.emailAddress || '-'}</Text>
                        </Grid.Col>
                        <Grid.Col span={12} sm={6}>
                          <Text fw={600} size="sm">Phone number</Text>
                          <Text>{values.consumerInformation?.phoneNumber || '-'}</Text>
                        </Grid.Col>
                        <Grid.Col span={12} sm={6}>
                          <Text fw={600} size="sm">Program or Department</Text>
                          <ConnectedProgramValue order={values} />
                        </Grid.Col>
                        <Grid.Col span={12} sm={6}>
                          <Text fw={600} size="sm">House or Neighborhood</Text>
                          <ConnectedHouseValue order={values} />
                        </Grid.Col>
                        <Grid.Col span={12} sm={6}>
                          <Text fw={600} size="sm">Items coming from</Text>
                          <Text>{values.isFromWarehouse ? 'Warehouse' : 'Store'}</Text>
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <Text fw={600} size="sm">Found all items?</Text>
                          <Text>{values.consumerInformation?.everythingFound ? 'Yes' : 'No'}</Text>
                        </Grid.Col>
                        {!values.consumerInformation?.everythingFound ? (
                          <Grid.Col span={12}>
                            <Text fw={600} size="sm">Missing items comment</Text>
                            <Text>{values.consumerInformation?.everythingFoundComment || '-'}</Text>
                          </Grid.Col>
                        ) : null}
                        <Grid.Col span={12}>
                          <Text fw={600} size="sm">Additional comments</Text>
                          <Text>{values.consumerInformation?.additionalComment || '-'}</Text>
                        </Grid.Col>
                      </>
                    </Grid>
                  </Grid.Col>
                  <Grid.Col span={12} lg={6}>
                    <>
                      <h2 style={{ color: AppTheme.colors.themeGreen, marginBottom: '0.5rem' }}>Items to Withdraw</h2>
                      {(() => {
                        const items = values.orderItems ?? [];
                        if (items.length === 0) {
                          return <Text color="dimmed">No items selected.</Text>;
                        }
                        let totalCents = 0;
                        return (
                          <div>
                            {items.map((oi, idx) => {
                              const inv = getItemById(oi.itemId);
                              const unitCents = inv?.estimatedCost ?? 0;
                              const qtyStore = oi.storeQuantity ?? 0;
                              const qtyWh = oi.warehouseQuantity ?? 0;
                              const qty = qtyStore + qtyWh;
                              const lineCents = unitCents * qty;
                              totalCents += lineCents;
                              return (
                                <div>
                                  <Divider my={4} variant="dashed" />
                                  <Grid key={idx} style={{ padding: '8px 0' }}>
                                    <Grid.Col span={8} sm={6}>
                                      <Text fw={600}>{inv?.name ?? 'Unknown item'}</Text>
                                      {qtyStore ? (
                                        <Text size="sm">Store Quantity: {qtyStore}</Text>
                                      ) : null}
                                      {qtyWh ? (
                                        <Text size="sm">Warehouse Quantity: {qtyWh}</Text>
                                      ) : null}
                                    </Grid.Col>
                                    <Grid.Col span={4} sm={6} styles={{height: '100%'}}>
                                      <Group position={'right'} align={'bottom'}>
                                        {unitCents > 0 && qty > 0 ? (
                                          <Text size="sm" color="dimmed">
                                            Estimated: {unitCents ? `(Unit ${formatCentsToUSD(unitCents)} x ${qty})` : ''}: {formatCentsToUSD(lineCents)}
                                          </Text>
                                        ) : (
                                          <Text size="sm" color="dimmed">Estimated: N/A</Text>
                                        )}
                                      </Group>
                                    </Grid.Col>
                                    {oi.extraComment ? (
                                      <Text size="sm">Notes: {oi.extraComment}</Text>
                                    ) : null}
                                    {oi.itemOptions && oi.itemOptions.length > 0 ? (
                                      <div style={{ marginTop: 4 }}>
                                        <Text size="sm" fw={600}>Options:</Text>
                                        {oi.itemOptions.map((opt, oidx) => (
                                          <Text key={oidx} size="sm">- {opt.name}: {opt.quantity}</Text>
                                        ))}
                                      </div>
                                    ) : null}
                                  </Grid>
                                </div>
                              );
                            })}
                            <Divider my={8} />
                            <Group position="apart">
                              <Text fw={600}>Estimated total</Text>
                              <Text fw={700}>{formatCentsToUSD(totalCents)}</Text>
                            </Group>
                          </div>
                        );
                      })()}
                    </>
                  </Grid.Col>
                  {/* Buttons now provided by StandardModal footer */}
                </Grid>
              ) : (
                <>
                  <OrderForm
                    items={props.inventoryUnarchived}
                    categories={props.categories}
                    title="Items to Withdraw"
                    showWarehouseQuantity={true}
                    thinnerCategories={true}
                    forceShowBothQuantities={true}
                  />
                </>
              )}
            </Form>
          )}
        </Formik>
    </StandardModal>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    upsertOrder: orderStore.actions.upsert,
    updateOrderStatus: orderStore.actions.updateOrderStatus
  }, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  inventoryUnarchived: itemStore.selectors.unarchivedItems(state),
  categories: categoryStore.selectors.getAsArray(state),
  getItemById: itemStore.selectors.getById(state)
});
export default connect(mapStateToProps, mapDispatchToProps)(OrderModal);
